import { useEffect, useState } from 'react';
import {
  getLayoutMainElement,
  readSpotlightClipBounds,
  readSpotlightRect,
} from '../../utils/onboardingSpotlightClip';
import {
  isOnboardingModalFooterTarget,
  queryOnboardingTargetElement,
} from '../../utils/onboardingTargetElement';
import { isCompactViewport } from '../../utils/onboardingTooltipPosition';

const ELEMENT_WAIT_TIMEOUT_MS = 5000;

export interface SpotlightElementState {
  spotlightRect: DOMRect | null;
  clipBounds: DOMRect | null;
  isModalTarget: boolean;
}

function findScrollContainer(element: Element): HTMLElement | null {
  const modalScroll = element.closest<HTMLElement>('[data-onboarding-modal-scroll]');
  if (modalScroll) {
    return modalScroll;
  }
  return getLayoutMainElement();
}

export function useSpotlightElement(targetId: string | null): SpotlightElementState {
  const [state, setState] = useState<SpotlightElementState>({
    spotlightRect: null,
    clipBounds: null,
    isModalTarget: false,
  });

  useEffect(() => {
    if (!targetId) {
      setState({ spotlightRect: null, clipBounds: null, isModalTarget: false });
      return;
    }

    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let resizeObserver: ResizeObserver | null = null;
    let mutationObserver: MutationObserver | null = null;
    let targetElement: Element | null = null;
    let scrollContainer: HTMLElement | null = null;

    const updateRect = () => {
      if (!targetElement) {
        return;
      }
      setState({
        spotlightRect: readSpotlightRect(targetElement),
        clipBounds: readSpotlightClipBounds(targetElement),
        isModalTarget: targetElement.closest('[data-onboarding-modal]') !== null,
      });
    };

    const attachToElement = (element: Element) => {
      targetElement = element;
      const scrollBlock =
        isCompactViewport() && isOnboardingModalFooterTarget(element) ? 'end' : 'center';
      element.scrollIntoView({ behavior: 'smooth', block: scrollBlock });
      updateRect();
      resizeObserver = new ResizeObserver(updateRect);
      resizeObserver.observe(element);

      scrollContainer = findScrollContainer(element);
      scrollContainer?.addEventListener('scroll', updateRect, { passive: true });
    };

    const tryFindElement = (): boolean => {
      const element = queryOnboardingTargetElement(targetId);
      if (!element) {
        return false;
      }
      attachToElement(element);
      return true;
    };

    const onScroll = () => {
      updateRect();
    };

    const onResize = () => {
      updateRect();
    };

    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);

    if (tryFindElement()) {
      return () => {
        window.removeEventListener('scroll', onScroll, true);
        window.removeEventListener('resize', onResize);
        scrollContainer?.removeEventListener('scroll', updateRect);
        resizeObserver?.disconnect();
      };
    }

    setState({
      spotlightRect: null,
      clipBounds: readSpotlightClipBounds(null),
      isModalTarget: false,
    });

    mutationObserver = new MutationObserver(() => {
      if (tryFindElement()) {
        mutationObserver?.disconnect();
        mutationObserver = null;
        if (timeoutId) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }
      }
    });

    mutationObserver.observe(document.body, { childList: true, subtree: true });

    timeoutId = setTimeout(() => {
      mutationObserver?.disconnect();
      mutationObserver = null;
      setState({
        spotlightRect: null,
        clipBounds: readSpotlightClipBounds(null),
        isModalTarget: false,
      });
    }, ELEMENT_WAIT_TIMEOUT_MS);

    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
      scrollContainer?.removeEventListener('scroll', updateRect);
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      mutationObserver?.disconnect();
      resizeObserver?.disconnect();
    };
  }, [targetId]);

  return state;
}
